import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { ClassName, GADataType, I18nText } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { ProductsSetUUID } from '../../../productsSetsTypes';

import {
  FETCH_PRODUCTS_SET_DOWNLOAD_URLS_QUERY,
  FetchProductsSetDownloadUrlsQueryResponse
} from '../../../queries/fetchProductsSetDownloadUrls.query';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useProductsSet } from '../../../hooks/useProductsSet';
import { useShowToastOnErrorChange } from '../../../../../common/hooks/useShowToastOnErrorChange';
import { useToastNotification } from '../../../../../common/hooks/useToastNotification';

import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { TooltipPlacement } from '../../../../../helpers/tooltips/tooltipsConstants';

import { downloadFile } from '../../../../../utils/downloadFile';
import { getFileNameFromURL } from '../../../../../utils/getFileNameFromURL';

import { ProductsSetCache } from '../../../ProductsSetCache';

import { words } from '../../../../../locales/keys';
import { CommonPermissions } from '../../../../common/commonConstants';

interface DownloadProductsSetSeparatelyButtonProps {
  className?: ClassName;
  i18nText?: I18nText;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  productsSetUuid: ProductsSetUUID;
  tooltipI18nText?: I18nText;
  tooltipPlacement?: TooltipPlacement;
  tooltipSingleton?: boolean;
}

function DownloadProductsSetSeparatelyButton({
  className,
  dataGa,
  i18nText,
  icon,
  iconClassName,
  productsSetUuid,
  tooltipI18nText,
  tooltipPlacement,
  tooltipSingleton
}: DownloadProductsSetSeparatelyButtonProps & GADataType) {
  const currentUser = useCurrentUser();

  const { productsSetLoading, refetchProductsSet, productsSetError } =
    useProductsSet<FetchProductsSetDownloadUrlsQueryResponse>({
      cacheKey: ProductsSetCache.downloadUrls(productsSetUuid),
      options: {
        enabled: false,
        enabledPlaceholder: false
      },
      query: FETCH_PRODUCTS_SET_DOWNLOAD_URLS_QUERY,
      uuid: productsSetUuid
    });

  const { showToastI18nNotification } = useToastNotification({
    appearance: 'success',
    i18nMessage: words.fileDownloadStarted
  });

  const withBlob = currentUser.hasPermissions(
    CommonPermissions.READ_DOWNLOADS_WITH_FETCH
  );

  useShowToastOnErrorChange({ error: productsSetError });

  const handleClick = useCallback<() => Promise<void>>(async () => {
    const refetchProductsSetResponse = await refetchProductsSet();

    const downloadUrls =
      refetchProductsSetResponse?.data?.productsSet?.downloadUrls;

    if (isEmpty(downloadUrls)) {
      return;
    }

    showToastI18nNotification();

    await Promise.all(
      map(downloadUrls, ({ url }) =>
        downloadFile({
          downloadFile: {
            name: getFileNameFromURL(url),
            file: url
          },
          withBlob
        })
      )
    );
  }, [refetchProductsSet, showToastI18nNotification, withBlob]);

  return (
    <PureTooltipIconButtonHelper
      className={className}
      dataGa={dataGa}
      disabled={productsSetLoading}
      i18nText={i18nText}
      icon={icon}
      iconClassName={iconClassName}
      onClick={handleClick}
      tooltipI18nText={tooltipI18nText}
      tooltipPlacement={tooltipPlacement}
      tooltipSingleton={tooltipSingleton}
    />
  );
}

export default DownloadProductsSetSeparatelyButton;
